import { useEffect, useState } from "react";
import api, { formatInr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data));
    api.get("/enquiries/mine").then((r) => setEnquiries(r.data));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="overline">My account</div>
      <h1 className="font-display text-4xl tracking-tight mt-2">Hello, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-[var(--fm-text-2)] mt-2 text-sm">{user?.email} · {user?.phone || "No phone"}</p>

      <div className="mt-10 border-b border-[var(--fm-border)] flex gap-8">
        {["orders", "enquiries"].map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`pb-3 text-sm tracking-wide uppercase ${tab === t ? "border-b-2 border-[var(--fm-primary)] text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)]"}`}>
            {t} ({t === "orders" ? orders.length : enquiries.length})
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "orders" ? (
          orders.length === 0 ? <Empty msg="No orders yet." /> : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.id}`} className="border border-[var(--fm-border)] p-6 bg-white">
                  <div className="flex flex-wrap justify-between gap-4">
                    <div>
                      <div className="overline">Order {o.id.slice(0, 8)}</div>
                      <div className="text-xs text-[var(--fm-text-3)] mt-1">{new Date(o.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-fm font-semibold text-[var(--fm-primary)]">{formatInr(o.total)}</div>
                      <StatusPill v={o.status} />
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-[var(--fm-text-2)] space-y-1">
                    {o.items.map((it, idx) => (<div key={idx}>{it.quantity} × {it.name}</div>))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          enquiries.length === 0 ? <Empty msg="No enquiries yet." /> : (
            <div className="space-y-4">
              {enquiries.map((e) => (
                <div key={e.id} className="border border-[var(--fm-border)] p-6 bg-white">
                  <div className="flex flex-wrap justify-between gap-4">
                    <div>
                      <div className="font-display text-lg">{e.product_name || "General enquiry"}</div>
                      <div className="text-xs text-[var(--fm-text-3)] mt-1">{new Date(e.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <StatusPill v={e.status} />
                  </div>
                  <p className="text-sm text-[var(--fm-text-2)] mt-3">{e.message}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="border border-dashed border-[var(--fm-border)] p-12 text-center text-sm text-[var(--fm-text-2)]">{msg}</div>;
}

function StatusPill({ v }) {
  const map = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    shipped: "bg-indigo-50 text-indigo-700 border-indigo-200",
    delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    contacted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border font-semibold ${map[v] || "bg-gray-50 text-gray-700 border-gray-200"}`}>{v}</span>;
}
